import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  DownloadTripWithFcrInnerJoinAsCsvResponse,
  QueryDownloadTripWithFcrInnerJoinAsCsvArgs,
  TripWithFcrInnerJoinFcrReporterEnum,
  TripWithFcrInnerJoinMaskInput,
  TripWithFcrInnerJoinSearch,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { TypedDocumentNode } from '@apollo/client';
import { getProperty, queryInput, QueryInputType } from '@/utils';
import graphApi from '@/api';
import { DownloadTripWithFcrInnerJoinAsCsvDocument } from '@/api/queries';

// Service Function Signature
type ServiceMethodPayload = ({
  from?: string | Date;
  to?: string | Date;
  airportCode?: string[];
  fcrReason?: string[];
  fcrReporter?: TripWithFcrInnerJoinFcrReporterEnum[];
  flightNumber?: string;
  providerId?: string[];
  servicerIataAirlineCode?: string[];
} & {})[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadTripWithFcrInnerJoinAsCsvResponse>;
}

const tripWithFcrInnerJoinCSVColumns: Partial<Record<keyof TripWithFcrInnerJoinMaskInput, boolean>> = {
  providerDisplayName: true,
  accidentIssueCount: false,
  airportCode: true,
  attendants: false,
  combineId: false,
  combineType: false,
  companyId: false,
  completionId: false,
  createdAt: false,
  createdBy: false,
  crewId: false,
  curbsideAt: false,
  deletedAt: false,
  deletedBy: false,
  dispatchIssueCount: false,
  doActualDatetime: false,
  doAirportCode: false,
  doLocationId: false,
  driverId: false,
  etaMinutes: false,
  fcrCause: true,
  fcrDescription: true,
  fcrId: false,
  fcrReason: true,
  fcrReporter: true,
  firstName: false,
  flightNumber: true,
  fromManifest: false,
  id: false,
  incidentIssueCount: false,
  isIllegalCombine: false,
  kind: false,
  lastName: false,
  latestScheduled: false,
  latestScheduledUtc: false,
  legacyId: false,
  loopName: false,
  lostAndFoundIssueCount: false,
  manifestImportId: false,
  offset: false,
  payerProviderId: false,
  pilots: false,
  plannedAt: false,
  providerId: false,
  puActualDatetime: false,
  puCompletedDatetime: false,
  puGpsDatetime: false,
  puLocationId: false,
  rateHistoryId: false,
  rateId: false,
  scheduled: false,
  scheduledUtc: true,
  servicerIataAirlineCode: false,
  split: false,
  status: false,
  traceId: false,
  trackFlightId: false,
  type: false,
  updatedAt: false,
  updatedBy: false,
  vehicleId: false,
};

export const { downloadTripWithFcrInnerJoinCsv, useTripWithFcrInnerJoinCsv } = ((): {
  downloadTripWithFcrInnerJoinCsv: ServiceMethod;
  useTripWithFcrInnerJoinCsv: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [refetch] = graphApi(DownloadTripWithFcrInnerJoinAsCsvDocument).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadTripWithFcrInnerJoinAsCsvResponse> => {
        const query: TripWithFcrInnerJoinSearch[] = payload?.map((item) => ({
          scheduledUtc: queryInput([item?.from, item?.to], QueryInputType.RANGE),
          airportCode: item?.airportCode.length ? queryInput(item?.airportCode) : null,
          fcrReason: item?.fcrReason.length ? queryInput(item?.fcrReason) : null,
          fcrReporter: item?.fcrReporter.length ? queryInput(item?.fcrReporter) : null,
          flightNumber: item?.flightNumber ? queryInput(item?.flightNumber) : null,
          providerId: item?.providerId.length ? queryInput(item?.providerId) : null,
          servicerIataAirlineCode: item?.servicerIataAirlineCode.length ? queryInput(item?.servicerIataAirlineCode) : null,
        }));
        const args: QueryDownloadTripWithFcrInnerJoinAsCsvArgs = {
          input: {
            query,
            columns: tripWithFcrInnerJoinCSVColumns,
          },
        };
        const opts = { merge: options?.merge ? 'downloadTripWithFcrInnerJoinAsCSV' : undefined };
        const res = await fn(args, opts);
        return getProperty('downloadTripWithFcrInnerJoinAsCSV', res);
      }
  );
  const useService = createGraphApiHook(refetch);
  return {
    downloadTripWithFcrInnerJoinCsv: refetch,
    useTripWithFcrInnerJoinCsv: useService,
  };
})();
